import { useState, useCallback } from "react";
import { useNavigate } from "react-router-dom";
import { listenerProfileIdentity } from "@/mocks/listenerProfileIdentity";
import { badgeCollection, badgeStats } from "@/mocks/listenerBadges";
import { currentListenerProfile } from "@/mocks/listenerLevels";
import { listenerTiers, tierColorMap } from "@/mocks/listenerTiers";
import { historyEntries, historyStats } from "@/mocks/listenerHistory";
import { favoriteRadios as favRadios, favoritePrograms, favoriteLocutores, favoriteStats } from "@/mocks/listenerFavorites";
import { savedEvents, savedLives } from "@/mocks/listenerCenter";
import { adminContests } from "@/mocks/adminContests";
import { adminPosts } from "@/mocks/adminPosts";
import AppNotificationsPanel from "./AppNotificationsPanel";
import AppBlockedPanel from "./AppBlockedPanel";
import Swal from "sweetalert2";

type ProfileSection = 'profile' | 'badges' | 'history' | 'favorites' | 'notifications' | 'blocked';
type FavoriteType = 'radios' | 'posts' | 'videos' | 'podcasts' | 'events' | 'contests';

function formatCount(n: number): string {
  if (n >= 1000000) return `${(n / 1000000).toFixed(1)}M`;
  if (n >= 1000) return `${(n / 1000).toFixed(1)}K`;
  return n.toString();
}

function timeAgo(iso: string): string {
  const now = new Date();
  const d = new Date(iso);
  const diffMin = Math.floor((now.getTime() - d.getTime()) / 60000);
  if (diffMin < 1) return "Ahora";
  if (diffMin < 60) return `Hace ${diffMin}m`;
  const diffHrs = Math.floor(diffMin / 60);
  if (diffHrs < 24) return `Hace ${diffHrs}h`;
  const diffDays = Math.floor(diffHrs / 24);
  if (diffDays === 1) return "Ayer";
  return `Hace ${diffDays}d`;
}

// Unified favorites state
interface UnifiedFavorite {
  id: string;
  type: FavoriteType;
  title: string;
  subtitle: string;
  image: string;
  link: string;
  meta: string;
  isLive?: boolean;
}

function buildUnifiedFavorites(): UnifiedFavorite[] {
  const items: UnifiedFavorite[] = [];

  favRadios.forEach((r) => items.push({
    id: `radio-${r.id}`,
    type: 'radios',
    title: r.name,
    subtitle: r.genre + ' · ' + r.location,
    image: r.image,
    link: `/radio/${r.slug}`,
    meta: formatCount(r.followers) + ' seguidores',
    isLive: r.isLive,
  }));

  adminPosts.slice(0, 5).forEach((p, i) => items.push({
    id: `post-${p.id || i}`,
    type: 'posts',
    title: p.title || p.content?.substring(0, 60) || 'Post sin título',
    subtitle: p.author || 'Radio RFW',
    image: p.imageUrl || p.thumbnailUrl || 'https://readdy.ai/api/search-image?query=Radio%20social%20media%20post%20content%20card%20with%20music%20notes%20and%20headphones%20on%20warm%20gradient%20background%20minimalist%20modern%20design%20social%20feed%20aesthetic&width=200&height=200&seq=fav-post-01&orientation=squarish',
    link: `/radio/${(p.stationName || 'radio-exito').toLowerCase().replace(/[^a-z0-9]+/g, '-')}`,
    meta: timeAgo(p.publishedAt || p.createdAt || new Date().toISOString()),
  }));

  // Videos represented by saved lives
  savedLives.slice(0, 4).forEach((l) => items.push({
    id: `video-${l.id}`,
    type: 'videos',
    title: l.programName,
    subtitle: l.stationName + ' · ' + l.hostName,
    image: l.coverUrl,
    link: `/radio/${l.stationName.toLowerCase().replace(/[^a-z0-9]+/g, '-')}`,
    meta: l.durationMinutes + ' min · ' + (l.status === 'finished' ? 'Finalizado' : l.status === 'live' ? 'En Vivo' : 'Próximo'),
    isLive: l.status === 'live',
  }));

  // Podcasts = favorite programs
  favoritePrograms.forEach((p) => items.push({
    id: `podcast-${p.id}`,
    type: 'podcasts',
    title: p.name,
    subtitle: p.station + ' · ' + p.host,
    image: p.image,
    link: `/program/${p.slug}`,
    meta: p.schedule,
    isLive: p.onAir,
  }));

  // Events
  savedEvents.forEach((e) => items.push({
    id: `event-${e.id}`,
    type: 'events',
    title: e.eventTitle,
    subtitle: e.stationName + ' · ' + e.eventType,
    image: e.imageUrl,
    link: `/radio/${e.stationName.toLowerCase().replace(/[^a-z0-9]+/g, '-')}`,
    meta: new Date(e.eventDate).toLocaleDateString('es-MX', { day: 'numeric', month: 'short' }) + ' · ' + e.eventTime,
  }));

  // Contests
  adminContests.slice(0, 4).forEach((c, i) => items.push({
    id: `contest-${c.id || i}`,
    type: 'contests',
    title: c.title || c.name || 'Concurso',
    subtitle: c.stationName || 'RFW',
    image: c.imageUrl || c.coverUrl || 'https://readdy.ai/api/search-image?query=Radio%20contest%20competition%20trophy%20announcement%20banner%20design%20warm%20vibrant%20colors%20festive%20atmosphere%20celebration%20broadcast%20event%20graphics&width=200&height=200&seq=fav-contest-01&orientation=squarish',
    link: `/radio/${(c.stationName || 'radio-exito').toLowerCase().replace(/[^a-z0-9]+/g, '-')}`,
    meta: c.prize || c.premio || 'Premio a definir',
  }));

  return items;
}

interface AppProfileTabProps {
  isActive: boolean;
}

export default function AppProfileTab({ isActive }: AppProfileTabProps) {
  const navigate = useNavigate();
  const [section, setSection] = useState<ProfileSection>('profile');
  const [favSection, setFavSection] = useState<FavoriteType>('radios');
  const [favorites, setFavorites] = useState<Set<string>>(() => new Set(buildUnifiedFavorites().map((f) => f.id)));

  const profile = listenerProfileIdentity;
  const levelInfo = currentListenerProfile;
  const userTier = listenerTiers.find((t) => t.level === 4);

  const unifiedFavs = buildUnifiedFavorites();
  const filteredFavs = unifiedFavs.filter((f) => {
    if (favSection === 'all') return true;
    return f.type === favSection;
  });

  const toggleFavorite = useCallback((id: string, title: string) => {
    setFavorites((prev) => {
      const next = new Set(prev);
      if (next.has(id)) {
        next.delete(id);
        Swal.fire({ toast: true, position: 'top-end', icon: 'success', title: `"${title}" quitado de favoritos`, showConfirmButton: false, timer: 1500, timerProgressBar: true });
      } else {
        next.add(id);
        Swal.fire({ toast: true, position: 'top-end', icon: 'success', title: `"${title}" agregado a favoritos`, showConfirmButton: false, timer: 1500, timerProgressBar: true });
      }
      return next;
    });
  }, []);

  const favTabs: { id: FavoriteType; label: string; icon: string }[] = [
    { id: 'radios', label: 'Radios', icon: 'ri-radio-line' },
    { id: 'posts', label: 'Posts', icon: 'ri-article-line' },
    { id: 'videos', label: 'Videos', icon: 'ri-video-line' },
    { id: 'podcasts', label: 'Podcasts', icon: 'ri-mic-line' },
    { id: 'events', label: 'Eventos', icon: 'ri-calendar-event-line' },
    { id: 'contests', label: 'Concursos', icon: 'ri-trophy-line' },
  ];

  const sections: { id: ProfileSection; label: string; icon: string; badge?: number }[] = [
    { id: 'profile', label: 'Perfil', icon: 'ri-user-line' },
    { id: 'badges', label: 'Insignias', icon: 'ri-award-line' },
    { id: 'history', label: 'Historial', icon: 'ri-history-line' },
    { id: 'favorites', label: 'Favoritos', icon: 'ri-heart-line' },
    { id: 'notifications', label: 'Notif.', icon: 'ri-notification-3-line' },
    { id: 'blocked', label: 'Bloqueos', icon: 'ri-shield-line' },
  ];

  return (
    <div className="h-full overflow-y-auto bg-background-50 scrollbar-none">
      {/* Header banner */}
      <div className="relative">
        <div className="h-36 bg-gradient-to-br from-orange-400 via-rose-400 to-amber-400 relative overflow-hidden">
          <div className="absolute inset-0 opacity-20">
            <div className="absolute top-4 left-4 w-32 h-32 rounded-full bg-white/30 blur-2xl"></div>
            <div className="absolute bottom-2 right-8 w-24 h-24 rounded-full bg-white/20 blur-xl"></div>
          </div>
          <div className="absolute top-4 right-4">
            <div className="flex items-center gap-1.5 px-3 py-1.5 rounded-full bg-white/20 backdrop-blur-sm text-white text-[11px] font-bold">
              <i className={`${userTier?.icon || 'ri-user-star-line'} text-sm`}></i>
              {userTier?.tierName || 'Top Fan'}
            </div>
          </div>
        </div>

        <div className="absolute left-1/2 -translate-x-1/2 -bottom-10 z-10">
          <div className="w-20 h-20 rounded-full border-4 border-background-50 overflow-hidden bg-background-100 shadow-sm">
            <img src={profile.photo} alt={profile.name} className="w-full h-full object-cover object-top" loading="lazy" />
          </div>
          <div className="absolute -bottom-1 -right-1 w-7 h-7 rounded-full bg-orange-500 border-2 border-background-50 flex items-center justify-center text-white text-[9px] font-bold">
            {profile.level.number}
          </div>
        </div>
      </div>

      {/* Info below avatar */}
      <div className="px-4 md:px-6 pt-12 pb-3 text-center">
        <h2 className="text-base font-bold text-foreground-950">{profile.name}</h2>
        <p className="text-[11px] text-foreground-500">{profile.username} · {profile.city}, {profile.country}</p>
        <p className="text-[11px] text-foreground-400 mt-0.5">Miembro desde {new Date(profile.joinDate).toLocaleDateString('es-ES', { year: 'numeric', month: 'long' })}</p>

        <div className="mt-3 max-w-xs mx-auto">
          <div className="flex items-center justify-between mb-1">
            <span className="text-[10px] font-bold text-foreground-600">{profile.level.name}</span>
            <span className="text-[10px] text-foreground-400">{profile.level.progress}% · {profile.level.nextLevel} ({profile.level.pointsToNext} pts)</span>
          </div>
          <div className="h-2 rounded-full bg-background-200 overflow-hidden">
            <div className="h-full rounded-full bg-gradient-to-r from-orange-400 to-rose-400 transition-all" style={{ width: `${profile.level.progress}%` }}></div>
          </div>
        </div>
      </div>

      {/* Stats row */}
      <div className="grid grid-cols-5 gap-1.5 px-4 md:px-6 mb-4">
        {[
          { label: 'Horas', value: formatCount(profile.stats.totalListeningHours), icon: 'ri-timer-line' },
          { label: 'Mensajes', value: profile.stats.messagesSent.toString(), icon: 'ri-chat-1-line' },
          { label: 'Favoritas', value: profile.stats.radiosFavorited.toString(), icon: 'ri-heart-line' },
          { label: 'Siguiendo', value: profile.stats.radiosFollowed.toString(), icon: 'ri-user-follow-line' },
          { label: 'Eventos', value: profile.stats.eventsAttended.toString(), icon: 'ri-calendar-event-line' },
        ].map((stat) => (
          <div key={stat.label} className="bg-background-50 border border-background-200 rounded-xl p-2 text-center">
            <div className="text-sm font-bold text-foreground-900">{stat.value}</div>
            <div className="text-[9px] text-foreground-400 mt-0.5">{stat.label}</div>
          </div>
        ))}
      </div>

      {/* Status cards */}
      <div className="grid grid-cols-3 gap-2 px-4 md:px-6 mb-4">
        <button onClick={() => navigate('/top-fans')} className="bg-gradient-to-br from-orange-50 to-amber-50 border border-orange-200 rounded-2xl p-3.5 text-center cursor-pointer hover:border-orange-300 transition-all">
          <div className="w-9 h-9 mx-auto rounded-xl bg-orange-100 flex items-center justify-center mb-1.5">
            <i className="ri-fire-fill text-orange-500 text-base"></i>
          </div>
          <p className="text-xs font-bold text-foreground-900">Top Fan</p>
          <p className="text-[9px] text-foreground-500 mt-0.5">#847 de 4,231</p>
          <span className="inline-block mt-1 px-2 py-0.5 rounded-full bg-orange-100 text-orange-700 text-[8px] font-bold">ACTIVO</span>
        </button>

        <button onClick={() => navigate('/club-vip')} className="bg-gradient-to-br from-rose-50 to-pink-50 border border-rose-200 rounded-2xl p-3.5 text-center cursor-pointer hover:border-rose-300 transition-all">
          <div className="w-9 h-9 mx-auto rounded-xl bg-rose-100 flex items-center justify-center mb-1.5">
            <i className="ri-vip-diamond-fill text-rose-500 text-base"></i>
          </div>
          <p className="text-xs font-bold text-foreground-900">VIP</p>
          <p className="text-[9px] text-foreground-500 mt-0.5">67% completado</p>
          <span className="inline-block mt-1 px-2 py-0.5 rounded-full bg-rose-100 text-rose-700 text-[8px] font-bold">EN PROGRESO</span>
        </button>

        <button onClick={() => navigate('/centro-gamificacion')} className="bg-gradient-to-br from-violet-50 to-purple-50 border border-violet-200 rounded-2xl p-3.5 text-center cursor-pointer hover:border-violet-300 transition-all">
          <div className="w-9 h-9 mx-auto rounded-xl bg-violet-100 flex items-center justify-center mb-1.5">
            <i className="ri-vip-crown-fill text-violet-500 text-base"></i>
          </div>
          <p className="text-xs font-bold text-foreground-900">Embajador</p>
          <p className="text-[9px] text-foreground-500 mt-0.5">32% completado</p>
          <span className="inline-block mt-1 px-2 py-0.5 rounded-full bg-violet-100 text-violet-700 text-[8px] font-bold">BLOQUEADO</span>
        </button>
      </div>

      {/* Activity row */}
      <div className="px-4 md:px-6 mb-4">
        <h3 className="text-xs font-bold text-foreground-400 uppercase tracking-wider mb-2">Actividad Reciente</h3>
        <div className="space-y-1.5">
          {profile.recentActivity.slice(0, 5).map((act) => (
            <div key={act.id} className="flex items-center gap-3 bg-background-50 border border-background-200 rounded-xl p-3">
              <div className={`w-9 h-9 rounded-xl flex items-center justify-center shrink-0 ${act.color}`}>
                <i className={`${act.icon} text-sm`}></i>
              </div>
              <div className="min-w-0 flex-1">
                <p className="text-xs text-foreground-800">{act.description}</p>
                <div className="flex items-center gap-2 mt-0.5">
                  <span className="text-[10px] text-foreground-400">{timeAgo(act.timestamp)}</span>
                  {act.stationName && (
                    <>
                      <span className="w-1 h-1 bg-foreground-300 rounded-full"></span>
                      <span className="text-[10px] text-foreground-500">{act.stationName}</span>
                    </>
                  )}
                </div>
              </div>
            </div>
          ))}
        </div>
      </div>

      {/* Section tabs */}
      <div className="flex gap-1.5 px-4 md:px-6 mb-3 overflow-x-auto scrollbar-none sticky top-0 z-20 bg-background-50 py-2">
        {sections.map((s) => (
          <button
            key={s.id}
            onClick={() => setSection(s.id)}
            className={`flex items-center gap-1.5 px-3.5 py-2 rounded-full text-xs font-semibold whitespace-nowrap transition-all cursor-pointer ${
              section === s.id
                ? 'bg-foreground-950 text-background-50'
                : 'bg-background-100 text-foreground-500 hover:bg-background-200'
            }`}
          >
            <i className={`${s.icon} text-[13px]`}></i>
            {s.label}
          </button>
        ))}
      </div>

      {/* Content */}
      <div className="px-4 md:px-6 pb-6">
        {/* Badges */}
        {section === 'badges' && (
          <div>
            <div className="flex items-center justify-between mb-3">
              <h3 className="text-sm font-bold text-foreground-950">Mis Insignias</h3>
              <span className="text-[10px] text-foreground-500">{badgeStats.earnedBadges}/{badgeStats.totalBadges} · {badgeStats.completionPercent}%</span>
            </div>
            <div className="grid grid-cols-2 gap-2.5">
              {badgeCollection.map((badge) => (
                <div
                  key={badge.id}
                  className={`rounded-2xl p-3.5 border transition-all ${
                    badge.earned
                      ? `${badge.borderColor} ${badge.colorLight}`
                      : 'border-background-200 bg-background-50 opacity-70'
                  }`}
                >
                  <div className="flex items-start gap-2.5">
                    <div className={`w-10 h-10 rounded-xl ${badge.earned ? badge.colorLight : 'bg-background-200'} flex items-center justify-center shrink-0`}>
                      <i className={`${badge.icon} ${badge.earned ? badge.textColor : 'text-foreground-400'} text-lg`}></i>
                    </div>
                    <div className="min-w-0 flex-1">
                      <div className="flex items-center gap-1.5">
                        <h4 className={`text-xs font-bold ${badge.earned ? 'text-foreground-900' : 'text-foreground-500'}`}>{badge.name}</h4>
                        {badge.earned && (
                          <span className="w-4 h-4 rounded-full bg-accent-500 flex items-center justify-center">
                            <i className="ri-check-line text-white text-[8px]"></i>
                          </span>
                        )}
                      </div>
                      <p className="text-[10px] text-foreground-500 mt-0.5 leading-relaxed">{badge.description}</p>
                      {!badge.earned && (
                        <div className="mt-2">
                          <div className="h-1.5 rounded-full bg-background-200 overflow-hidden">
                            <div className="h-full rounded-full bg-primary-400" style={{ width: `${badge.progress}%` }}></div>
                          </div>
                          <p className="text-[9px] text-foreground-400 mt-0.5">{badge.progressLabel || `${badge.progress}%`}</p>
                        </div>
                      )}
                    </div>
                  </div>
                </div>
              ))}
            </div>
          </div>
        )}

        {/* History */}
        {section === 'history' && (
          <div>
            <div className="flex items-center justify-between mb-3">
              <h3 className="text-sm font-bold text-foreground-950">Historial de Actividad</h3>
              <span className="text-[10px] text-foreground-500">{historyStats.totalEntries} registros · +{historyStats.totalPointsEarned} pts</span>
            </div>
            <div className="space-y-2">
              {historyEntries.map((entry) => {
                const entryLink = entry.type === 'radio' ? `/radio/${(entry.title || 'radio').toLowerCase().replace(/[^a-z0-9]+/g, '-')}` :
                  entry.type === 'programa' ? `/program/${(entry.title || 'programa').toLowerCase().replace(/[^a-z0-9]+/g, '-')}` :
                  entry.type === 'evento' ? '/events' :
                  entry.type === 'concurso' ? '/events' : '/radios';
                return (
                <div
                  key={entry.id}
                  onClick={() => navigate(entryLink)}
                  className="flex items-start gap-3 bg-background-50 border border-background-200 hover:border-foreground-300 rounded-2xl p-3.5 transition-all cursor-pointer">
                  <div className="w-11 h-11 rounded-xl bg-background-100 border border-background-200 flex items-center justify-center shrink-0 overflow-hidden">
                    {entry.image ? (
                      <img src={entry.image} alt={entry.title} className="w-full h-full object-cover object-top" loading="lazy" />
                    ) : (
                      <i className="ri-history-line text-foreground-400 text-lg"></i>
                    )}
                  </div>
                  <div className="min-w-0 flex-1">
                    <div className="flex items-center gap-2">
                      <h4 className="text-sm font-bold text-foreground-900 truncate">{entry.title}</h4>
                      <span className={`px-1.5 py-0.5 rounded-full text-[8px] font-bold uppercase shrink-0 ${
                        entry.type === 'radio' ? 'bg-rose-50 text-rose-600' :
                        entry.type === 'programa' ? 'bg-emerald-50 text-emerald-600' :
                        entry.type === 'evento' ? 'bg-violet-50 text-violet-600' :
                        entry.type === 'concurso' ? 'bg-amber-50 text-amber-600' :
                        entry.type === 'saludo' ? 'bg-sky-50 text-sky-600' :
                        'bg-primary-50 text-primary-600'
                      }`}>
                        {entry.type === 'radio' ? 'Radio' :
                         entry.type === 'programa' ? 'Prog.' :
                         entry.type === 'evento' ? 'Evento' :
                         entry.type === 'concurso' ? 'Concurso' :
                         entry.type === 'saludo' ? 'Saludo' : 'Canción'}
                      </span>
                    </div>
                    <p className="text-[11px] text-foreground-600 mt-0.5">{entry.subtitle}</p>
                    <p className="text-[10px] text-foreground-400 mt-0.5 line-clamp-2">{entry.description}</p>
                    <p className="text-[9px] text-foreground-400 mt-1">{timeAgo(entry.timestamp)}</p>
                  </div>
                </div>
              );
              })}
            </div>
          </div>
        )}

        {/* Favorites - Unified */}
        {section === 'favorites' && (
          <div>
            {/* Favorite sub-tabs */}
            <div className="flex gap-1.5 mb-3 overflow-x-auto scrollbar-none">
              {favTabs.map((ft) => {
                const count = unifiedFavs.filter((f) => f.type === ft.id).length;
                return (
                  <button
                    key={ft.id}
                    onClick={() => setFavSection(ft.id)}
                    className={`flex items-center gap-1.5 px-3 py-2 rounded-full text-xs font-semibold whitespace-nowrap transition-all cursor-pointer ${
                      favSection === ft.id
                        ? 'bg-accent-500 text-background-50'
                        : 'bg-background-100 text-foreground-500 hover:bg-background-200'
                    }`}
                  >
                    <i className={`${ft.icon} text-[13px]`}></i>
                    {ft.label}
                    <span className={`px-1.5 py-0.5 rounded-full text-[9px] font-bold ${
                      favSection === ft.id ? 'bg-background-50/20 text-background-50' : 'bg-background-200 text-foreground-500'
                    }`}>{count}</span>
                  </button>
                );
              })}
            </div>

            {/* Favorites list */}
            {filteredFavs.length === 0 ? (
              <div className="bg-background-50 rounded-2xl p-10 text-center border border-background-200">
                <div className="w-14 h-14 mx-auto mb-3 rounded-full bg-background-100 flex items-center justify-center">
                  <i className="ri-heart-line text-2xl text-foreground-400"></i>
                </div>
                <p className="text-foreground-600 text-sm">No tenés {favTabs.find((f) => f.id === favSection)?.label.toLowerCase()} guardados</p>
                <p className="text-foreground-400 text-xs mt-1">Tocá el corazón para guardar contenido</p>
              </div>
            ) : (
              <div className="space-y-1.5">
                {filteredFavs.map((fav) => {
                  const isFav = favorites.has(fav.id);
                  return (
                    <div
                      key={fav.id}
                      onClick={() => navigate(fav.link)}
                      className="flex items-center gap-3 bg-background-50 border border-background-200 hover:border-foreground-300 rounded-xl p-3 transition-all cursor-pointer group"
                    >
                      <div className="w-11 h-11 rounded-xl overflow-hidden bg-background-100 border border-background-200 shrink-0 relative">
                        <img src={fav.image} alt={fav.title} className="w-full h-full object-cover object-top" loading="lazy" />
                        {fav.isLive && (
                          <div className="absolute -top-0.5 -right-0.5 w-2.5 h-2.5 rounded-full bg-red-500 border-2 border-background-50 animate-pulse"></div>
                        )}
                      </div>
                      <div className="min-w-0 flex-1">
                        <div className="flex items-center gap-2">
                          <h4 className="text-xs font-bold text-foreground-900 truncate">{fav.title}</h4>
                          {fav.isLive && (
                            <span className="flex items-center gap-1 px-1.5 py-0.5 rounded-full bg-red-50 text-red-600 text-[8px] font-bold shrink-0">
                              <span className="w-1 h-1 rounded-full bg-red-500 animate-pulse"></span>LIVE
                            </span>
                          )}
                        </div>
                        <p className="text-[10px] text-foreground-500 mt-0.5">{fav.subtitle}</p>
                        <p className="text-[9px] text-foreground-400 mt-0.5">{fav.meta}</p>
                      </div>
                      <button
                        onClick={(e) => { e.stopPropagation(); toggleFavorite(fav.id, fav.title); }}
                        className={`w-8 h-8 flex items-center justify-center rounded-lg transition-all cursor-pointer shrink-0 ${
                          isFav ? 'text-accent-500 hover:bg-accent-50' : 'text-foreground-300 hover:text-accent-500 hover:bg-accent-50'
                        }`}
                        title={isFav ? 'Quitar de favoritos' : 'Guardar en favoritos'}
                      >
                        <i className={`${isFav ? 'ri-heart-fill' : 'ri-heart-line'} text-lg`}></i>
                      </button>
                    </div>
                  );
                })}
              </div>
            )}
          </div>
        )}

        {/* Notifications */}
        {section === 'notifications' && (
          <AppNotificationsPanel />
        )}

        {/* Blocked Users & Reports */}
        {section === 'blocked' && (
          <AppBlockedPanel />
        )}
      </div>

      {/* Edit profile button */}
      <div className="px-4 md:px-6 pb-6">
        <button
          onClick={() => {
            Swal.fire({
              title: 'Editar Perfil',
              html: `
                <div class="text-left space-y-3">
                  <div>
                    <label class="block text-xs font-semibold text-left mb-1">Nombre</label>
                    <input id="swal-profile-name" class="w-full px-3 py-2 border border-gray-200 rounded-xl text-sm text-left" value="${profile.name}" />
                  </div>
                  <div>
                    <label class="block text-xs font-semibold text-left mb-1">Ciudad</label>
                    <input id="swal-profile-city" class="w-full px-3 py-2 border border-gray-200 rounded-xl text-sm text-left" value="${profile.city}" />
                  </div>
                </div>
              `,
              icon: 'info',
              showCancelButton: true,
              confirmButtonText: 'Guardar cambios',
              cancelButtonText: 'Cancelar',
              confirmButtonColor: '#f97316',
              cancelButtonColor: '#d1d5db',
              customClass: { popup: 'rounded-2xl', title: 'font-bold', confirmButton: 'rounded-lg px-5 py-2 text-sm font-bold whitespace-nowrap', cancelButton: 'rounded-lg px-5 py-2 text-sm font-bold whitespace-nowrap' },
              buttonsStyling: false,
              preConfirm: () => {
                const name = (document.getElementById('swal-profile-name') as HTMLInputElement)?.value;
                const city = (document.getElementById('swal-profile-city') as HTMLInputElement)?.value;
                if (!name?.trim()) {
                  Swal.showValidationMessage('El nombre es obligatorio');
                  return false;
                }
                return { name, city };
              },
            }).then((result) => {
              if (result.isConfirmed) {
                Swal.fire({ toast: true, position: 'top-end', icon: 'success', title: 'Perfil actualizado', showConfirmButton: false, timer: 2000, timerProgressBar: true });
              }
            });
          }}
          className="w-full py-3 rounded-xl bg-background-100 border border-background-200 hover:bg-background-200 text-foreground-600 font-semibold text-sm cursor-pointer whitespace-nowrap transition-all"
        >
          <i className="ri-settings-3-line mr-1.5"></i>
          Editar perfil
        </button>
      </div>
    </div>
  );
}